import type {UserMediaStatus} from '@pexip/media';
import {
    isDeviceInUse,
    isGrantedAudio,
    isGrantedVideo,
    isInitialPermissionsNotGranted,
    isRejected,
    isRejectedOnlyAudio,
    isRejectedOnlyVideo,
} from '@pexip/media';

interface RequestedMedia {
    audio: boolean;
    video: boolean;
}

/**
 * Checks whether a self-view has an enabled video track.
 *
 * @example isVideoActive(false, stream)
 */
export const isVideoActive = (
    isVideoInputMuted: boolean,
    mediaStream?: MediaStream,
) => !isVideoInputMuted && Boolean(mediaStream?.getVideoTracks().length);

/**
 * Checks whether the self-view should render its avatar fallback.
 *
 * @example isVideoMuted(true, streamStatus)
 */
export const isVideoMuted = (
    isVideoInputMuted: boolean,
    streamStatus?: UserMediaStatus,
) =>
    !streamStatus ||
    !isInitialPermissionsNotGranted(streamStatus) ||
    (isVideoInputMuted && isGrantedVideo(streamStatus));

/**
 * Checks whether requested media failures require a self-view error border.
 *
 * @example shouldShowErrorBorder(streamStatus, {audio: true, video: true})
 */
export const shouldShowErrorBorder = (
    streamStatus: UserMediaStatus | undefined,
    {audio, video}: RequestedMedia,
) =>
    !!streamStatus &&
    (isRejected(streamStatus) ||
        (video &&
            (isRejectedOnlyVideo(streamStatus) ||
                (isGrantedAudio(streamStatus) &&
                    !isGrantedVideo(streamStatus)))) ||
        (audio &&
            (isRejectedOnlyAudio(streamStatus) ||
                (isGrantedVideo(streamStatus) &&
                    !isGrantedAudio(streamStatus)))) ||
        isDeviceInUse(streamStatus));
